import { GetServerSideProps, NextPage } from 'next';
import Script from 'next/script';
import React from 'react';

import { MiniatureSizesEnum } from '~/api/book/bookApiTypes';
import { pagesConfigStore } from '~/atomic/atom/links/pagesConfigStore';
import { getImageUrlBySize, sortByType } from '~/atomic/atom/SimpleCover';
import { LayoutType } from '~/atomic/layout/DefaultLayout';
import { FullScreenLayout } from '~/atomic/layout/FullScreenLayout';
import { PageTemplate } from '~/atomic/organism/PageTemplate/PageTemplate';
import { ReaderTextBook } from '~/atomic/page/book-reader/ReaderTextBook';
import { closeAudioPlayer } from '~/feature/audio/audioPlayer.data';
import { checkAuthToken } from '~/feature/authorization/checkAuthToken';
import { getAuthTokenFromServer } from '~/feature/authorization/getAuthToken';
import { getGraphqlBookWithChapter } from '~/feature/book/book.data';
import {
  bookSelector,
  currentGraphqlBookAnnotationMetaSelector,
} from '~/feature/book/book.selector';
import { TransactionLoader } from '~/feature/book/paymentResult/TransactionLoader';
import { getPublishedChapterListByBookSlugFromGraphql } from '~/feature/chapter/chapter.data';
import { reactionActions } from '~/feature/myBooks/reactions/reaction.slice';
import { isPersonalRecsEnabledFromCtx } from '~/feature/recsys/personalRecsAccess';
import { saveUserFavoriteGenres } from '~/feature/saveUserFavoriteGenres';
import { isSupportWebpSelector } from '~/feature/supportWebp/isSupportWebp.selector';
import { AddAuthorMetrika } from '~/feature/yandex/addAuthorMetrika';
import { saveMoveOnTrackingBookriverRecommendsBook } from '~/feature/yandex/trackingBuyBookriverRecommends';
import { saveMoveOnTrackingHotNewsBook } from '~/feature/yandex/trackingBuyHotNews';
import { saveMoveOnTrackingRecentlyUpdateBook } from '~/feature/yandex/trackingBuyRecentlyUpdate';
import { saveMoveOnTrackingAdvertBook } from '~/feature/yandex/trakingBuyAdvertBook';
import {
  createStore, RootState, useAppDispatch, useAppSelector,
} from '~/store';

const ReaderPage:NextPage & LayoutType = () => {
  const dispatch = useAppDispatch();
  const { currentGraphqlBook } = useAppSelector(bookSelector);
  const { isSupport } = useAppSelector(isSupportWebpSelector);
  const currentGraphqlBookAnnotationMeta = useAppSelector(currentGraphqlBookAnnotationMetaSelector);

  const authorName = currentGraphqlBook?.author?.name ?? '';
  const bookName = currentGraphqlBook?.name ?? '';

  const titleParts = [bookName, authorName].filter(Boolean);
  const title = titleParts.length > 0
    ? `${titleParts.join(' ')} читать онлайн | Новые произведения на Bookriver`
    : 'Читать онлайн | Новые произведения на Bookriver';

  const cycleInfo = currentGraphqlBook?.cycle?.numInCycle
    ? `Книга #${currentGraphqlBook.cycle.numInCycle}. `
    : '';
  const description = `${cycleInfo}${currentGraphqlBookAnnotationMeta || 'Читайте книги онлайн на BookRiver'}`;

  let imageUrl;
  if (currentGraphqlBook?.coverImages) {
    const { jpeg, webp } = sortByType(currentGraphqlBook.coverImages);

    if (isSupport) {
      imageUrl = getImageUrlBySize(webp, MiniatureSizesEnum.L);
    } else {
      imageUrl = getImageUrlBySize(jpeg, MiniatureSizesEnum.L);
    }
  }

  return (
    <PageTemplate
      isPublic={pagesConfigStore.reader.isPublic}
      onLoadPage={async () => {
        // Некритичные операции (трекинг) - fire-and-forget
        Promise.all([
          dispatch(saveUserFavoriteGenres({ genres: currentGraphqlBook?.genres })),
          dispatch(saveMoveOnTrackingAdvertBook({ book: currentGraphqlBook })),
          dispatch(saveMoveOnTrackingHotNewsBook({ book: currentGraphqlBook })),
          dispatch(saveMoveOnTrackingRecentlyUpdateBook({ book: currentGraphqlBook })),
          dispatch(saveMoveOnTrackingBookriverRecommendsBook({ book: currentGraphqlBook })),
        ]).catch((err) => console.error('Tracking error:', err));

        dispatch(closeAudioPlayer());
      }}
      seo={{
        title,
        description,
        image: imageUrl,
        type: 'book',
        canonical: pagesConfigStore.reader.as(currentGraphqlBook?.slug),
        customSeoTags: (
          <>
            <meta name="author" content={authorName} />
            <meta name="robots" content="follow" />
          </>
        ),
      }}
      content={(
        <>
          <ReaderTextBook />
          <AddAuthorMetrika metrikaId={currentGraphqlBook?.author?.yandexMetrika} />
          <TransactionLoader />
          <Script
            strategy="lazyOnload"
            onLoad={() => {
              dispatch(reactionActions.setIsLoadLibGif(true));
            }}
            src="/js/libgif.js"
          />
        </>
      )}
    />
  );
};

ReaderPage.getLayout = (page) => {
  return (
    <FullScreenLayout>{page}</FullScreenLayout>
  );
};

export const getServerSideProps: GetServerSideProps<
{ state: RootState; },
{ bookSlug: string; }
> = async (context) => {
  const { dispatch, store } = await createStore();
  await checkAuthToken(context, dispatch);
  const token = getAuthTokenFromServer(context);
  const bookSlug = context.params?.bookSlug ?? '';

  const stateAfterAuth = store.getState();
  if (stateAfterAuth.user.serviceUnavailable) {
    context.res.statusCode = 503;
    return { props: { state: stateAfterAuth } };
  }

  await Promise.all([
    dispatch(getGraphqlBookWithChapter({
      slug: bookSlug as string,
      token,
      showPersonalFit: isPersonalRecsEnabledFromCtx(context, store.getState().user.user),
    })),
    dispatch(getPublishedChapterListByBookSlugFromGraphql({
      slug: bookSlug, token, includeStats: false,
    })),
  ]);

  const state = store.getState();
  if (state.user.serviceUnavailable) {
    context.res.statusCode = 503;
    return { props: { state } };
  }

  const { currentGraphqlBook } = state.book;

  if (!currentGraphqlBook
    || !(currentGraphqlBook && currentGraphqlBook?.id)) {
    return { notFound: true };
  }

  return { props: { state } };
};

export default ReaderPage;
